import { useEffect, useState } from 'react';
import { Styled } from './Styled';
import icons from '../../../../img/icons/icons';
import { CertificadosServices, ICertificado } from '../../../Services/api/certificados/Certificados.services';
import { pdfsServices } from '../../../Services/api/pdfs/pdfsServices';
import { CoursesServices } from '../../../Services/api/courses/courses.services';
import { Api } from '../../../Services/api/axios-config';

interface FullCertificadoProps {
    codigoCTR: string;
    onClose: () => void;
}

export const FullCertificado: React.FC<FullCertificadoProps> = ({ codigoCTR, onClose }) => {
    const [certificado, setCertificado] = useState<ICertificado | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [cursoNome, setCursoNome] = useState<string>('');
    const [cursoHoras, setCursoHoras] = useState<number>(0);
    const [pdfUrl, setPdfUrl] = useState<string>('');

    useEffect(() => {
        const fetchCertificado = async () => {
            setLoading(true);
            setError('');
            
            try {
                const result = await CertificadosServices.getByCTR(codigoCTR);
                
                if (result instanceof Error) {
                    setError('Não foi possível localizar este certificado');
                    setLoading(false);
                    return;
                }
                
                setCertificado(result);
                
                // Buscar informações do curso
                if (result.cursoId) {
                    const cursoResult = await CoursesServices.getCourseById(result.cursoId);
                    if (!(cursoResult instanceof Error)) {
                        setCursoNome(cursoResult.Name || '');
                        setCursoHoras(cursoResult.Workload || 0);
                    }
                }
                
                // Preparar visualização do PDF
                if (result.pdfId) {
                    try {
                        const response = await Api.get(`/pdfs/download/${result.pdfId}`, {
                            responseType: 'blob'
                        });
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        setPdfUrl(url);
                    } catch (err) {
                        console.error('Erro ao carregar PDF:', err);
                    }
                }
                
                setLoading(false);
            } catch (err) {
                setError('Erro ao buscar certificado');
                setLoading(false);
            }
        };
        
        fetchCertificado();
        
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [codigoCTR]);

    const handleDownload = async () => {
        if (certificado?.pdfId) {
            try {
                await pdfsServices.downloadPdf(certificado.pdfId);
            } catch (error) {
                alert('Erro ao baixar certificado');
            }
        }
    };

    if (loading) {
        return (
            <Styled.Content>
                <Styled.LoadingMessage>Carregando...</Styled.LoadingMessage>
            </Styled.Content>
        );
    }

    if (error || !certificado) {
        return (
            <Styled.Content>
                <Styled.ErrorMessage>
                    <p>{error || 'Não foi possível localizar este certificado'}</p>
                    <button onClick={onClose}>Fechar</button>
                </Styled.ErrorMessage>
            </Styled.Content>
        );
    }

    return (
        <Styled.Content>
            <Styled.Header>
                <Styled.LogoArea>
                    <img src={icons.CertificadoPremium} alt="Certificado Premium" />
                </Styled.LogoArea>
                <Styled.Actions>
                    <Styled.DownloadButton onClick={handleDownload}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16Z" fill="white"/>
                            <path d="M6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.196 19.021 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8043 19.021 19.413 19.413C19.021 19.8043 18.55 20 18 20H6Z" fill="white"/>
                        </svg>
                    </Styled.DownloadButton>
                    <Styled.CloseButton onClick={onClose}>
                        ✕
                    </Styled.CloseButton>
                </Styled.Actions>
            </Styled.Header>
            
            <Styled.InfoBar>
                <span><strong>Aluno:</strong> {certificado.aluno}</span>
                <span><strong>Curso:</strong> {cursoNome}</span>
                {cursoHoras > 0 && <span><strong>{cursoHoras} Horas</strong></span>}
                <span><strong>CTR:</strong> {certificado.codigoCTR}</span>
            </Styled.InfoBar>
            
            <Styled.PdfViewer>
                {pdfUrl ? (
                    <iframe
                        src={pdfUrl}
                        title="Certificado"
                        width="100%"
                        height="100%"
                    />
                ) : (
                    <p>Não foi possível carregar o certificado</p>
                )}
            </Styled.PdfViewer>
        </Styled.Content>
    );
};